
// Custom hook for theme management
import {useState} from "react";
import { Typography, Spin } from 'antd';
import React from 'react';
import {useAuth} from "@/hooks/auth/use-auth";
import {useTheme} from "@/components/common/ThemeProvider";
import {useTransactionList} from "@/hooks/accounts/useCustomerAccount";
import {formatAmount} from "@/components/common/utils/money";
import {Link} from "react-router-dom";
import {AuthUser,TransactionDTO} from "@/shared/api/model";
import {useCanSelfRegister} from "@/modules/onboarding/hooks/useSelfOnboardingConfig";


const { Title } = Typography;


// Types
interface BalanceCardProps {
    title: string;
    amount: string;
    currency: string;
    dark: boolean;
}

interface MemberInfoProps {
    pfNo: string;
    membershipNo: string;
    dark: boolean;
}

interface InfoRowProps {
    label: string;
    value: string;
    dark: boolean;
}

interface ShortcutsProps {
    dark: boolean;
}

interface ShortcutButtonProps {
    text: string;
    icon: string;
    color: string;
    dark: boolean;
}

// Balance Card Component
export const BalanceCard: React.FC<BalanceCardProps> = ({ title, amount, currency, dark }) => (
    <div className={`p-6 rounded-lg ${dark ? 'bg-gray-800' : 'bg-white'}`}>
        <h2 className={`text-sm mb-2 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>{title}</h2>
        <span className={`text-xl font-bold ${dark ? 'text-green-400' : 'text-green-600'} mb-1 mr-3`}>{currency}</span>
        <span className={`text-xl font-bold ${dark ? 'text-green-400' : 'text-green-600'}`}>{amount}</span>
    </div>
);

// Member Information Component
export const MemberInfo: React.FC<MemberInfoProps> = ({pfNo, membershipNo, dark }) => {

    const {isLoading,error,user} = useAuth();
    if(isLoading) return <p>Loading ...</p>
    if(error || user == undefined) return <p>Something went wrong</p>;


    return (
        <div className={`p-6 rounded-lg ${dark ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className={`text-xl font-bold mb-4 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>Member's
                Information</h2>
            <div className="space-y-3">
                <InfoRow label="Full Name:" value={user.fullname||""} dark={dark}/>
                <InfoRow label="PF No.:" value={pfNo} dark={dark}/>
                <InfoRow label="Membership No:" value={membershipNo} dark={dark}/>
            </div>
        </div>
    );
}

export const InfoRow: React.FC<InfoRowProps> = ({ label, value, dark }) => (
    <div className="flex justify-between">
        <span className={dark ? 'text-gray-400' : 'text-gray-600'}>{label}</span>
        <span className={dark ? 'text-gray-100' : 'text-gray-800'}>{value}</span>
    </div>
);

interface ShortcutButtonProps {
    text: string;
    icon: string;
    color: string;
    dark: boolean;
    link: string;
}

const ShortcutButton: React.FC<ShortcutButtonProps> = ({ text, icon, color, dark, link }) => (
    <Link
        to={link}
        className={`
            flex items-center justify-start p-3 rounded-lg transition-colors duration-200
            ${dark
            ? `bg-gray-700 hover:bg-gray-600 text-gray-200`
            : `bg-gray-100 hover:bg-gray-200 text-gray-800`
        }
            w-full
        `}
    >
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 mr-3 ${color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
        </svg>
        <span className="font-medium">{text}</span>
    </Link>
);

interface ShortcutsProps {
    dark: boolean;
}

export const Shortcuts: React.FC<ShortcutsProps> = ({ dark }) => {
    const [{isLoading,data: canSelfRegisterLoading}] = useCanSelfRegister();
    if(isLoading) return <p>Loading ...</p>;
    return (
        <div>
            <h2 className={`text-xl font-bold mb-4 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>Important
                Shortcuts</h2>
            <div className="grid grid-cols-1 gap-4">
                <ShortcutButton text="Apply Loan" icon="M12 6v6m0 0v6m0-6h6m-6 0H6" color="text-green-500" dark={dark}
                                link="/loans/application"/>
                <ShortcutButton text="Account Statement"
                                icon="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                color="text-purple-500" dark={dark} link="/accounts/activities"/>
                <ShortcutButton text="Account Balances"
                                icon="M12 8c-3.314 0-6 1.343-6 3s2.686 3 6 3 6-1.343 6-3-2.686-3-6-3zm0 7c-2.67 0-5.073-.84-6-2v3c0 1.657 2.686 3 6 3s6-1.343 6-3v-3c-.927 1.16-3.33 2-6 2z"
                                color="text-indigo-500" dark={dark} link="/accounts/balances"/>
                <ShortcutButton text="Loan Status" icon="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                color="text-yellow-500" dark={dark} link="/loans/my-applications"/>
            </div>
        </div>
    );
}


interface Transaction {
    type: string;
    description: string;
    date: string;
    amount: number;
}

interface TransactionsProps {
    authUser: AuthUser;
    dark: boolean;
}

export const TransactionItem: React.FC<{ transaction: TransactionDTO; dark: boolean }> = ({ transaction, dark }) => (
    <div className={`flex justify-between items-center py-4 border-b ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${dark ? 'bg-green-800' : 'bg-green-100'} mr-4`}>
                <span className={`text-lg ${dark ? 'text-green-200' : 'text-green-800'}`}>-</span>
            </div>
            <div>
                <h3 className={`font-semibold ${dark ? 'text-white' : 'text-gray-800'}`}>{transaction.productName}</h3>
                <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-600'}`}>{transaction.narration}</p>
            </div>
        </div>
        <div className="text-right">
            <p className={`font-semibold text-green-500`}>{formatAmount(transaction.amount||0)}</p>
            <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-600'}`}>{transaction.trxDateH}</p>
        </div>
    </div>
);

export const RecentTransactions: React.FC<TransactionsProps> = ({ authUser, dark }) => {
    const [{isLoading,isError,data:transactions}] = useTransactionList(authUser.branch ||"",authUser.custNo ||"");
    if(isLoading) return <p>Loading ...</p>;
    if(isError || transactions == undefined) return <p>Something went wrong</p>;
    return (
        <div className={`rounded-lg p-6 ${dark ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className={`text-xl font-bold mb-4 ${dark ? 'text-gray-100' : 'text-gray-800'}`}>
                Recent Savings And Shares Transactions
            </h2>
            <div className="space-y-2">
                {transactions.map((transaction, index) => (
                    <TransactionItem key={index} transaction={transaction} dark={dark} />
                ))}
            </div>
        </div>
    );


};



export const Greetings: React.FC = () => {
    const { dark } = useTheme();
    const { isLoading, error, user } = useAuth();

    if (isLoading) return <Spin size="large" />;
    if (error) return <Typography.Text type="danger">Something went wrong</Typography.Text>;

    return (
        <header className={`mb-6 mt-3 ${dark ? 'bg-gray-800' : 'bg-white'}`}>
            <Title
                level={2}
                className={dark ? 'text-gray-100' : 'text-gray-800'}
                style={{ margin: 0 }}
            >
                Welcome, {user?.fullname}.
            </Title>
        </header>
    );
};

export { default as LoanEligibilityCard } from './LoanEligibilityCard';
